import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ✅ List blogs (unchanged)
export const fetchBlogs = createAsyncThunk(
  'blogs/fetchBlogs',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/blogs', { params: filters });
      // backend returns { success, data: { blogs, pagination } }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch blogs'
      );
    }
  }
);

// ✅ NEW: Fetch blog by slug instead of id
export const fetchBlogBySlug = createAsyncThunk(
  'blogs/fetchBlogBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blogs/${slug}`);
      // backend returns { success, data: blog }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch blog'
      );
    }
  }
);

// Featured blogs (unchanged)
export const fetchFeaturedBlogs = createAsyncThunk(
  'blogs/fetchFeaturedBlogs',
  async (limit = 3, { rejectWithValue }) => {
    try {
      const response = await api.get(`/blogs/featured?limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch featured blogs'
      );
    }
  }
);

const initialState = {
  blogs: [],
  featuredBlogs: [],
  currentBlog: null,
  loading: false,
  featuredLoading: false,
  currentBlogLoading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalBlogs: 0,
    hasNext: false,
    hasPrev: false,
    limit: 12
  },
  filters: {}
};

const blogSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {};
    },
    clearCurrentBlog: (state) => {
      state.currentBlog = null;
      state.currentBlogLoading = false;
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // 🔹 Fetch Blogs (list)
      .addCase(fetchBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.loading = false;

        if (action.payload?.data?.blogs) {
          state.blogs = action.payload.data.blogs;
          state.pagination =
            action.payload.data.pagination || initialState.pagination;
        } else if (action.payload?.blogs) {
          state.blogs = action.payload.blogs;
          state.pagination =
            action.payload.pagination || initialState.pagination;
        } else if (Array.isArray(action.payload)) {
          state.blogs = action.payload;
        } else {
          state.blogs = [];
        }
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.blogs = [];
      })

      // 🔹 Fetch Blog By Slug (detail)
      .addCase(fetchBlogBySlug.pending, (state) => {
        state.currentBlogLoading = true;
        state.error = null;
        state.currentBlog = null;
      })
      .addCase(fetchBlogBySlug.fulfilled, (state, action) => {
        state.currentBlogLoading = false;

        if (action.payload?.data) {
          // { success, data: blog }
          state.currentBlog = action.payload.data;
        } else if (action.payload?.blog) {
          // { blog: {...} }
          state.currentBlog = action.payload.blog;
        } else {
          state.currentBlog = action.payload;
        }
      })
      .addCase(fetchBlogBySlug.rejected, (state, action) => {
        state.currentBlogLoading = false;
        state.error = action.payload || action.error.message;
        state.currentBlog = null;
      })

      // 🔹 Fetch Featured Blogs
      .addCase(fetchFeaturedBlogs.pending, (state) => {
        state.featuredLoading = true;
        state.error = null;
      })
      .addCase(fetchFeaturedBlogs.fulfilled, (state, action) => {
        state.featuredLoading = false;

        if (action.payload?.data?.blogs) {
          state.featuredBlogs = action.payload.data.blogs;
        } else if (action.payload?.blogs) {
          state.featuredBlogs = action.payload.blogs;
        } else if (Array.isArray(action.payload)) {
          state.featuredBlogs = action.payload;
        } else {
          state.featuredBlogs = [];
        }
      })
      .addCase(fetchFeaturedBlogs.rejected, (state, action) => {
        state.featuredLoading = false;
        state.error = action.payload || action.error.message;
        state.featuredBlogs = [];
      });
  }
});

// Actions
export const {
  clearError,
  setFilters,
  clearFilters,
  clearCurrentBlog
} = blogSlice.actions;

// Selectors
export const selectBlogs = (state) => state.blogs.blogs;
export const selectFeaturedBlogs = (state) => state.blogs.featuredBlogs;
export const selectCurrentBlog = (state) => state.blogs.currentBlog;
export const selectBlogLoading = (state) => state.blogs.loading;
export const selectFeaturedBlogsLoading = (state) =>
  state.blogs.featuredLoading;
export const selectCurrentBlogLoading = (state) =>
  state.blogs.currentBlogLoading;
export const selectBlogError = (state) => state.blogs.error;
export const selectBlogPagination = (state) => state.blogs.pagination;
export const selectBlogFilters = (state) => state.blogs.filters;

export default blogSlice.reducer;
